import { Component, TemplateFunction } from '@ribajs/core'
import { TwService } from '../modules/tailwind/services'

const HEADER_ICONS_SELECTOR = '.header__icons'
const HEADER_HAMBURGER_SELECTOR = '.header__icon--hamburger'
const SESSION_STORAGE_KEY = 'sb-logo'

interface Scope {
  /** Set this to `true` to force the animation play even if this is not the first visit */
  forceAnimation: boolean
  /** True if the logo animation was played and is complete */
  completeForNow: boolean
  paying: boolean
  /** If true the animation is only shown once per browser session */
  onlyOnSession: boolean
}

/**
 * A logo component to show an animated logo on mobile
 */
export class SbLogoComponent extends Component {
  /** The tag name of the element, in this case `<sb-logo>...</sb-logo>` */
  public static tagName = 'sb-logo'

  lifecycleOptions = {
    ignore: true,
  }

  /** Used to detect breakpoint changes */
  protected tailwind = TwService.getSingleton()

  protected svgAniEl: SVGElement | null = null

  /** Used to hide the icons in the header if the display is very small and has not enough space for the Logo animation */
  protected headerIconsWrapperEl: HTMLDivElement | null = null
  /** Used to hide the icons in the header if the display is very small and has not enough space for the Logo animation */
  protected headerHamburgerEl: SVGElement | null = null

  public scope: Scope = {
    forceAnimation: false,
    completeForNow: false,
    paying: false,
    // If the router is disabled we use the onlyOnSession feature by default
    onlyOnSession: !window.router_enable,
  }

  constructor() {
    super()
    this.onAnimationComplete = this.onAnimationComplete.bind(this)
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return ['force-animation', 'only-on-session']
  }

  protected requiredAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbLogoComponent.observedAttributes)
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  async beforeBind() {
    await super.beforeBind()
    this.getElements()
    this.addEventListeners()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    this.checkAnimation()
    this.classList.add(`${SbLogoComponent.tagName}-ready`)
    setTimeout(() => {
      this.checkAnimation()
    }, 5000)
    await super.afterBind()
  }

  async afterAllBind() {
    this.checkAnimation()
    await super.afterAllBind()
  }

  public startAnimation() {
    if (this.scope.paying) {
      return
    }

    if (this.scope.forceAnimation) {
      this.resetAnimation()
    }

    this.scope.paying = true
    //this.hideElements()
    this.svgAniEl?.classList.add('icon-logo-animated')
    setTimeout(() => {
      this.onAnimationComplete()
    }, 4000)
  }

  /** Reset the animation by jumping back to the first frame */
  public resetAnimation() {
    this.scope.completeForNow = false
    this.svgAniEl?.classList.remove('icon-logo-animated', 'icon-logo-small')
  }

  public hideElements() {
    if (this.headerIconsWrapperEl) {
      this.headerIconsWrapperEl.classList.remove('opacity-100')
      this.headerIconsWrapperEl.classList.add('opacity-0')
    }
    if (this.headerHamburgerEl) {
      this.headerHamburgerEl.classList.remove('opacity-100')
      this.headerHamburgerEl.classList.add('opacity-0')
    }
  }

  public showFullLogo() {
    this.svgAniEl?.classList.remove('icon-logo-animated', 'icon-logo-small')
  }

  public showSmallLogo() {
    this.svgAniEl?.classList.add('icon-logo-small')
    this.svgAniEl?.classList.remove('icon-logo-animated')
  }

  public showElements() {
    if (this.headerIconsWrapperEl) {
      this.headerIconsWrapperEl.classList.remove('opacity-0')
      this.headerIconsWrapperEl.classList.add('opacity-100')
    }
    if (this.headerHamburgerEl) {
      this.headerHamburgerEl.classList.remove('opacity-0')
      this.headerHamburgerEl.classList.add('opacity-100')
    }
  }

  /** Returns `true` if the animation was played and is complete */
  public isCompleteForNow() {
    return this.scope.completeForNow
  }

  /** if the animation has already been played at some time this returns `true` */
  public isCompleteALongTime() {
    return (
      this.scope.onlyOnSession &&
      sessionStorage.getItem(SESSION_STORAGE_KEY) === 'complete' &&
      !this.scope.forceAnimation
    )
  }

  public setCompleteALongTime() {
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'complete')
    return this.isCompleteALongTime()
  }

  /**
   * Starts the animation when all conditions are met
   * Conditions:
   *  - Mobile breakpoint
   *  - Animation was not played in this session yet
   */
  public checkAnimation() {
    if (this.tailwind.isActiveBreakpointSmallerThan('md')) {
      if (!this.isCompleteForNow() && !this.isCompleteALongTime()) {
        this.startAnimation()
      } else {
        this.showSmallLogo()
        this.showElements()
      }
    } else {
      this.showElements()
      this.showFullLogo()
    }
  }

  /** Called as soon as the animation is complete */
  protected onAnimationComplete() {
    this.showElements()
    this.scope.paying = false
    this.scope.completeForNow = true
    this.setCompleteALongTime()
  }

  protected getElements() {
    this.headerIconsWrapperEl = document.querySelector(HEADER_ICONS_SELECTOR)
    this.headerHamburgerEl = document.querySelector(HEADER_HAMBURGER_SELECTOR)

    this.svgAniEl = document.getElementById(
      'seeberger_logo_animated',
    ) as SVGElement | null

    if (!this.headerIconsWrapperEl) {
      console.warn(
        `"${HEADER_ICONS_SELECTOR}" element not found, did you changed the class names? Then please also adjust the selector.`,
      )
    }

    if (!this.headerHamburgerEl) {
      console.warn(
        `"${HEADER_HAMBURGER_SELECTOR}" element not found, did you changed the class names? Then please also adjust the selector.`,
      )
    }
  }

  protected addEventListeners() {
    this.tailwind.on('breakpoint:changed', this.checkAnimation, this)
  }

  protected removeEventListeners() {
    this.tailwind.off('breakpoint:changed', this.checkAnimation, this)
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    this.classList.remove('opacity-0')

    // Do not replace the innerHTML if the animation was already played in this session
    if (this.isCompleteALongTime()) {
      return null
    }
    return null
  }
}
